import type { ReactNode, Ref } from 'react'
import { clsx } from 'clsx'
import { t } from '@/lang'

type Variant = 'info' | 'warning' | 'error'

const variants = {
  info: {
    style: 'bg-complementary-200 border-primary-500 text-primary-700',
    prefix: null,
    role: undefined,
  },
  warning: {
    style: 'bg-warning-100 border-warning-700 text-warning-700',
    prefix: t('common.warning-prefix'),
    role: 'status',
  },
  error: {
    style: 'bg-error-100 border-error-600 text-error-700',
    prefix: t('common.error-prefix'),
    role: undefined,
  },
} as const satisfies Record<Variant, { style: string; prefix: string | null; role?: 'status' | 'alert' }>

type Props = {
  variant?: Variant
  icon?: ReactNode
  tabIndex?: number
  ref?: Ref<HTMLDivElement>
  'data-testid'?: string
  className?: string
  children: ReactNode
}

export function Banner({ variant = 'info', icon, tabIndex, ref, className, children, ...rest }: Props) {
  const { style, prefix, role } = variants[variant]

  return (
    <div
      {...rest}
      ref={ref}
      role={role}
      tabIndex={tabIndex}
      className={clsx('flex items-start gap-2.5 border p-3.75 text-banner', style, className)}
    >
      {icon ? <span className="shrink-0">{icon}</span> : null}
      <div>
        {prefix ? <span className="sr-only">{prefix}</span> : null}
        {children}
      </div>
    </div>
  )
}
